// src/components/home/ExecutionCenter.tsx
//
// Execution Center V2 — the primary deterministic operating surface for Home.
// Hero layout, summary strip, ordered collapsible sections, and specific deep
// links. Pure data + deterministic services; no AI, no generated text.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useGetTasksQuery } from '../../api/tasksApi';
import { useGetProjectsQuery } from '../../api/projectsApi';
import { useGetGoalsQuery } from '../../api/goalsApi';
import { useGetEventsQuery } from '../../api/eventsApi';
import { useGetOpportunitiesQuery } from '../../api/opportunitiesApi';
import { useGetReferralsQuery } from '../../api/referralsApi';
import { useHomeContactInsights } from '../../hooks/useHomeContactInsights';
import {
  deriveProjectInterventions,
  type Intervention,
  type InterventionType,
} from '../../services/interventionEngine';
import { computeProjectHealth } from '../../services/projectHealth';
import { computeWorkloadSummary } from '../../services/workload';
import { deriveDailyFocus } from '../../services/dailyExecution';
import { computeTodayPlan } from '../../services/todayPlan';
import { derivePeopleAttention } from '../../services/peopleAttention';
import WorkspacePriorities from './WorkspacePriorities';

const TYPE_BADGE: Record<InterventionType, { label: string; cls: string }> = {
  focus: { label: 'Focus', cls: 'bg-yellow-100 text-yellow-800' },
  review: { label: 'Review', cls: 'bg-blue-100 text-blue-800' },
  unblock: { label: 'Unblock', cls: 'bg-red-100 text-red-800' },
  reassign: { label: 'Reassign', cls: 'bg-purple-100 text-purple-800' },
  estimate: { label: 'Estimate', cls: 'bg-gray-100 text-gray-700' },
  defer: { label: 'Defer', cls: 'bg-indigo-100 text-indigo-800' },
};
const SEVERITY_RANK = { high: 0, medium: 1, low: 2 } as const;
const WORKLOAD_CHIP = {
  overloaded: { label: 'Overloaded', cls: 'text-red-700 bg-red-100' },
  balanced: { label: 'Balanced', cls: 'text-green-700 bg-green-100' },
  underloaded: { label: 'Light load', cls: 'text-gray-600 bg-gray-100' },
} as const;
const LOAD_CHIP = {
  light: 'text-gray-600 bg-gray-100',
  normal: 'text-blue-700 bg-blue-100',
  heavy: 'text-red-700 bg-red-100',
} as const;
const FOCUS_VS_CAPACITY_LABEL = {
  fits: 'Focus fits free time',
  tight: 'Tight — focus ≈ free time',
  over: 'Focus exceeds free time',
  unknown: 'Add task estimates to compare',
} as const;

const hours = (min: number) => `${Math.round((min / 60) * 10) / 10}h`;

/** Specific deep link from an intervention's related ids (falls back to actionPath). */
function interventionLink(i: Intervention): string {
  if (i.relatedTaskId) return `/tasks?taskId=${i.relatedTaskId}`;
  if (i.relatedMilestoneId && i.relatedProjectId) return `/projects?projectId=${i.relatedProjectId}&milestoneId=${i.relatedMilestoneId}`;
  if (i.relatedProjectId) return `/projects?projectId=${i.relatedProjectId}`;
  if (i.relatedContactId) return `/contacts?contactId=${i.relatedContactId}`;
  return i.actionPath ?? '/tasks';
}

function Section({ title, count, defaultOpen, children }: { title: string; count?: number; defaultOpen: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-gray-100 pt-3 first:border-t-0 first:pt-0">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {title}{count != null ? ` (${count})` : ''}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && children}
    </div>
  );
}

interface ExecutionCenterProps {
  workspaceId: string;
  forUserId?: string | null;
  baseInterventions?: Intervention[];
}

export default function ExecutionCenter({ workspaceId, forUserId, baseInterventions = [] }: ExecutionCenterProps) {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const weekAhead = useMemo(() => {
    const d = new Date(`${today}T00:00:00`);
    d.setDate(d.getDate() + 7);
    return d.toISOString();
  }, [today]);

  const { data: tasksData } = useGetTasksQuery({ workspaceId }, { skip: !workspaceId });
  const { data: projects } = useGetProjectsQuery({ workspaceId }, { skip: !workspaceId });
  const { data: goalsData } = useGetGoalsQuery({ workspaceId, status: 'active' }, { skip: !workspaceId });
  const { data: events } = useGetEventsQuery({ workspaceId, from: `${today}T00:00:00.000Z`, to: weekAhead }, { skip: !workspaceId });
  const { data: opportunities } = useGetOpportunitiesQuery({ workspaceId, status: 'open' }, { skip: !workspaceId });
  const { data: referrals } = useGetReferralsQuery({ workspaceId }, { skip: !workspaceId });
  const { insights: contactInsights } = useHomeContactInsights(workspaceId);

  const tasks = useMemo(() => tasksData?.tasks ?? [], [tasksData]);
  const projectList = useMemo(() => projects ?? [], [projects]);
  const goals = useMemo(() => goalsData?.goals ?? [], [goalsData]);
  const eventList = useMemo(() => events ?? [], [events]);

  const projectInterventions = useMemo(
    () => deriveProjectInterventions({ projects: projectList, tasks, today }),
    [projectList, tasks, today],
  );

  const attention = useMemo(() => {
    const merged = [...baseInterventions, ...projectInterventions];
    const seen = new Set<string>();
    return merged
      .filter((i) => (seen.has(i.id) ? false : (seen.add(i.id), true)))
      .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity])
      .slice(0, 5);
  }, [baseInterventions, projectInterventions]);

  const focus = useMemo(
    () => deriveDailyFocus({ tasks, projects: projectList, goals, today, forUserId, max: 5 }),
    [tasks, projectList, goals, today, forUserId],
  );

  const workload = useMemo(
    () => computeWorkloadSummary(tasks, eventList, { today, forUserId }),
    [tasks, eventList, today, forUserId],
  );

  const todayPlan = useMemo(
    () => computeTodayPlan(eventList, focus, workload, { today }),
    [eventList, focus, workload, today],
  );

  const people = useMemo(
    () =>
      derivePeopleAttention({
        insights: contactInsights.map((ci) => ({
          contactId: ci.contactId,
          name: ci.context.contact.fullName,
          signals: ci.context.signals,
        })),
        opportunities: opportunities ?? [],
        referrals: referrals ?? [],
        today,
      }),
    [contactInsights, opportunities, referrals, today],
  );

  const projectsAtRisk = useMemo(
    () =>
      projectList
        .filter((p) => p.status !== 'completed' && p.status !== 'cancelled')
        .map((p) => ({ project: p, health: computeProjectHealth(p, { today }) }))
        .filter((x) => x.health.health !== 'healthy')
        .sort((a, b) => (a.health.health === 'at_risk' ? -1 : 1) - (b.health.health === 'at_risk' ? -1 : 1))
        .slice(0, 5),
    [projectList, today],
  );

  const milestonesDueSoon = useMemo(() => {
    const out: { projectId: string; milestoneId: string; projectName: string; name: string; days: number; behind: boolean }[] = [];
    for (const p of projectList) {
      for (const m of p.milestones ?? []) {
        if (m.status === 'completed' || !m.dueDate) continue;
        const days = Math.round((new Date(`${m.dueDate.split('T')[0]}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 86_400_000);
        if (days >= 0 && days <= 7) {
          out.push({ projectId: p.id, milestoneId: m.id, projectName: p.name, name: m.name, days, behind: m.progress?.trackingStatus === 'behind' });
        }
      }
    }
    return out.sort((a, b) => a.days - b.days).slice(0, 5);
  }, [projectList, today]);

  const dueLabel = (days: number) => (days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`);
  const timeRange = (startIso: string, endIso: string) =>
    `${new Date(startIso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}–${new Date(endIso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;

  return (
    <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 ring-1 ring-blue-50 space-y-4">
      {/* Hero header + summary strip */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-gray-900">Execution Center</h2>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${WORKLOAD_CHIP[workload.status].cls}`}>
            {WORKLOAD_CHIP[workload.status].label}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { n: attention.length, label: 'Need Attention' },
            { n: projectsAtRisk.length, label: 'Projects At Risk' },
            { n: focus.length, label: 'Focus Items' },
            { n: people.length, label: 'People' },
          ].map((s) => (
            <span key={s.label} className="text-xs px-2 py-1 rounded-lg bg-gray-50 border border-gray-200 text-gray-700">
              <span className="font-semibold text-gray-900">{s.n}</span> {s.label}
            </span>
          ))}
        </div>
      </div>

      {/* 1. Today */}
      <Section title="Today" defaultOpen>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
          <div className="p-2 rounded-lg bg-gray-50">
            <p className="text-xs text-gray-500">Meetings</p>
            <p className="text-sm font-semibold text-gray-900">{todayPlan.meetingCount} · {hours(todayPlan.meetingMinutes)}</p>
          </div>
          <div className="p-2 rounded-lg bg-gray-50">
            <p className="text-xs text-gray-500">Meeting load</p>
            <span className={`inline-block text-xs px-1.5 py-0.5 rounded-full font-medium capitalize ${LOAD_CHIP[todayPlan.meetingLoad]}`}>{todayPlan.meetingLoad}</span>
          </div>
          <div className="p-2 rounded-lg bg-gray-50">
            <p className="text-xs text-gray-500">Free time</p>
            <p className="text-sm font-semibold text-gray-900">{hours(todayPlan.freeMinutes)}</p>
          </div>
          <div className="p-2 rounded-lg bg-gray-50">
            <p className="text-xs text-gray-500">Focus workload</p>
            <p className="text-sm font-semibold text-gray-900">{hours(todayPlan.focusWorkloadMinutes)}</p>
          </div>
          <div className="p-2 rounded-lg bg-gray-50 col-span-2 sm:col-span-1">
            <p className="text-xs text-gray-500">Focus vs capacity</p>
            <p className={`text-xs font-medium ${todayPlan.focusVsCapacity === 'over' ? 'text-red-600' : todayPlan.focusVsCapacity === 'tight' ? 'text-amber-600' : 'text-gray-700'}`}>
              {FOCUS_VS_CAPACITY_LABEL[todayPlan.focusVsCapacity]}
            </p>
          </div>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Deep work windows (≥90m)</p>
          {todayPlan.deepWorkWindows.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No deep-work windows today.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {todayPlan.deepWorkWindows.map((w, i) => (
                <span key={i} className="text-xs text-green-800 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded">
                  {timeRange(w.start, w.end)} ({hours(w.minutes)})
                </span>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* 2. What Needs Attention */}
      <Section title="What Needs Attention" count={attention.length} defaultOpen>
        {attention.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing flagged — you're in good shape.</p>
        ) : (
          <ul className="space-y-2">
            {attention.map((i) => {
              const cfg = TYPE_BADGE[i.type];
              return (
                <li key={i.id} className="flex items-start gap-2">
                  <span className={`flex-shrink-0 mt-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${cfg.cls}`}>{cfg.label}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{i.title}</p>
                    <p className="text-xs text-gray-500"><span className="font-medium text-gray-600">Why:</span> {i.reason}</p>
                    <button onClick={() => navigate(interventionLink(i))} className="mt-0.5 text-xs text-blue-600 hover:underline">
                      {i.suggestedActionLabel} →
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* 3. Today's Focus */}
      <Section title="Today's Focus" count={focus.length} defaultOpen>
        {focus.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing urgent or high-priority right now.</p>
        ) : (
          <ul className="space-y-2">
            {focus.map((f) => (
              <li key={f.task.id}>
                <button onClick={() => navigate(`/tasks?taskId=${f.task.id}`)} className="w-full flex items-start gap-2 text-left hover:bg-gray-50 rounded px-1 py-1">
                  <span className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 truncate block">{f.task.title}</span>
                    <span className="mt-0.5 flex flex-wrap gap-1">
                      {f.reasons.map((r, i) => (
                        <span key={i} className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-full">{r}</span>
                      ))}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* 4. People Requiring Attention */}
      <Section title="People Requiring Attention" count={people.length} defaultOpen>
        {people.length === 0 ? (
          <p className="text-sm text-gray-500">No one needs follow-up right now.</p>
        ) : (
          <ul className="space-y-1.5">
            {people.map((p, idx) => (
              <li key={p.contactId ?? `loose-${idx}`}>
                <button onClick={() => navigate(p.actionPath)} className="w-full flex items-start gap-2 text-left hover:bg-gray-50 rounded px-1 py-0.5">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 bg-blue-400" />
                  <span className="flex-1 min-w-0 text-sm text-gray-800">
                    <span className="font-medium">{p.name}</span>
                    <span className="text-xs text-gray-500"> — {p.reason}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* 5. Projects At Risk (collapsed) */}
      <Section title="Projects At Risk" count={projectsAtRisk.length} defaultOpen={false}>
        {projectsAtRisk.length === 0 ? (
          <p className="text-sm text-gray-500">All active projects are healthy.</p>
        ) : (
          <ul className="space-y-1.5">
            {projectsAtRisk.map(({ project, health }) => (
              <li key={project.id}>
                <button onClick={() => navigate(`/projects?projectId=${project.id}`)} className="w-full flex items-start gap-2 text-left hover:bg-gray-50 rounded px-1 py-0.5">
                  <span className={`flex-shrink-0 mt-0.5 text-xs px-1.5 py-0.5 rounded-full font-medium ${health.health === 'at_risk' ? 'text-red-700 bg-red-100' : 'text-amber-700 bg-amber-100'}`}>
                    {health.label}
                  </span>
                  <span className="flex-1 min-w-0 text-sm text-gray-800 truncate">
                    {project.name}
                    <span className="text-xs text-gray-500"> — {health.reasons.join(', ')}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* 6. Milestones Due Soon (collapsed) */}
      <Section title="Milestones Due Soon" count={milestonesDueSoon.length} defaultOpen={false}>
        {milestonesDueSoon.length === 0 ? (
          <p className="text-sm text-gray-500">No milestones due in the next 7 days.</p>
        ) : (
          <ul className="space-y-1.5">
            {milestonesDueSoon.map((m) => (
              <li key={m.milestoneId}>
                <button onClick={() => navigate(`/projects?projectId=${m.projectId}&milestoneId=${m.milestoneId}`)} className="w-full flex items-center gap-2 text-left hover:bg-gray-50 rounded px-1 py-0.5">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${m.behind ? 'bg-red-500' : 'bg-amber-500'}`} />
                  <span className="flex-1 min-w-0 truncate text-sm text-gray-700">
                    <span className="font-medium">{m.name}</span>
                    <span className="text-xs text-gray-500"> · {m.projectName} · due {dueLabel(m.days)}{m.behind ? ' · behind' : ''}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* 7. Capacity (collapsed) */}
      <Section title="Capacity" defaultOpen={false}>
        <ul className="space-y-1">
          {workload.messages.map((msg, i) => (
            <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${workload.status === 'overloaded' ? 'bg-red-500' : workload.status === 'balanced' ? 'bg-green-500' : 'bg-gray-400'}`} />
              {msg}
            </li>
          ))}
        </ul>
      </Section>

      {/* 8. Workspace Priorities — additive server Execution Engine section (flag-gated;
          renders nothing when disabled). Personal "Today's Focus" above is unchanged. */}
      <WorkspacePriorities workspaceId={workspaceId} />
    </div>
  );
}
